/* Appligator Factory Graphics: manifest-driven Canvas 2D graphics upgrade view. */
(function () {
  const { useCallback, useEffect, useMemo, useRef, useState } = React;
  const Icon = window.Icon || (() => null);

  const SCENE = Object.freeze({ width: 1420, height: 700, tileW: 52, tileH: 26, originX: 530, originY: 76 });
  const ZONE_POINTS = Object.freeze({
    dispatch: Object.freeze([[10.5, 6.2], [11.5, 6.2], [12.5, 6.2], [13.5, 6.2]]),
    archive: Object.freeze([[3.2, 4.7], [5.2, 4.7], [4.2, 5.8]]),
    records: Object.freeze([[3.3, 10.3], [6.3, 10.3], [3.4, 13.3], [6.4, 13.3]]),
    mailroom: Object.freeze([[4.8, 15.4], [6.2, 15.6], [3.6, 14.4]]),
    steamworks: Object.freeze([[13.7, 4.2], [14.4, 12.4], [19.7, 3.1]]),
    atelier: Object.freeze([[10.8, 16.3], [12.8, 16.5], [11.8, 15.4]]),
    workshop: Object.freeze([[12.4, 8.9], [17.4, 8.9], [14.7, 9.5]]),
    common: Object.freeze([[19.5, 14.4], [20.6, 16.0], [21.7, 14.6], [18.8, 15.7]]),
  });

  function norm(value) { return String(value || "").trim().toLowerCase(); }
  function hashStr(value) {
    let hash = 0;
    const text = String(value || "factory");
    for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) | 0;
    return Math.abs(hash);
  }
  function workRunId(item) { return item && (item.runId || item.id || ""); }
  function workTitle(item) { return String(item && (item.title || item.displayLabel || item.currentWorkTitle) || "Untitled work").trim(); }
  function workStatus(item) { return norm(item && (item.status || item.state || item.representativeStatus)) || "idle"; }
  function changedAtMillis(value) {
    const millis = Date.parse(value || "");
    return Number.isFinite(millis) ? millis : 0;
  }
  function latestWorkTime(item) {
    return item && (item.updatedAt || item.lastEventAt || (item.timing && item.timing.updatedAt) || (item.timing && item.timing.createdAt) || "");
  }
  function workCategory(item) {
    let category = norm(item && (item.category || item.representativeCategory || (item.semanticEvent && item.semanticEvent.category) || (item.animationIntent && item.animationIntent.category)));
    if (category.includes("sketch")) return "sketch";
    const text = norm(`${item && item.title || ""} ${item && item.detail || ""} ${item && item.displayLabel || ""}`);
    if ((category === "" || category === "document" || category === "note" || category === "generic_tool_work") && /sketch|draw(ing)?\b|paint|canvas|diagram|wireframe|mock-?up|illustrat/.test(text)) return "sketch";
    return category || "generic_tool_work";
  }
  function employeeStateFromWorkItem(item) {
    const state = workStatus(item);
    if (state.includes("restricted") || state.includes("denied") || state.includes("permission")) return "restricted";
    if (state.includes("fail") || state.includes("error")) return "failed";
    if (state.includes("block") || state.includes("need")) return "blocked";
    if (state.includes("queue")) return "queued";
    if (state.includes("delegat")) return "delegated";
    if (state.includes("complete") || state.includes("done")) return "completed";
    if (state.includes("run") || state.includes("work") || state.includes("progress")) return "running";
    return item ? (item.state || item.status || "running") : "idle";
  }
  function isActiveEmployee(employee) {
    const state = norm(employee && employee.state);
    return state.includes("run") || state.includes("work") || state.includes("queue") || state.includes("delegat") || state.includes("pending");
  }
  function officeChangeFromChange(change) {
    const officeChange = change && change.details && change.details.officeChange;
    return officeChange && officeChange.contractVersion === "office-action-center.v1" ? officeChange : null;
  }
  function isFactoryRefreshChange(change) {
    const toolName = norm(change && change.toolName);
    return toolName.startsWith("action-center/") || change && change.kind === "employee-activity" && change.jobId || change && change.kind === "tool-call" && change.aiEmployeeId;
  }
  function mergeWorkItemIntoSnapshot(snapshot, officeChange) {
    const workItem = officeChange && officeChange.workItem;
    const runId = workRunId(workItem);
    const employeeId = workItem && workItem.employeeId;
    if (!snapshot || !workItem || !runId || !employeeId) return null;
    const work = Array.isArray(snapshot.work) ? snapshot.work.slice() : [];
    const index = work.findIndex((item) => String(workRunId(item)) === String(runId));
    if (index >= 0) work[index] = { ...work[index], ...workItem };
    else work.push(workItem);
    work.sort((a, b) => changedAtMillis(latestWorkTime(b)) - changedAtMillis(latestWorkTime(a)));
    const employees = (Array.isArray(snapshot.employees) ? snapshot.employees : []).map((employee) => {
      if (String(employee && employee.id) !== String(employeeId)) return employee;
      return {
        ...employee,
        state: employeeStateFromWorkItem(workItem),
        currentRunId: runId,
        currentWorkTitle: workTitle(workItem),
        lastActivityAt: latestWorkTime(workItem) || employee.lastActivityAt,
      };
    });
    return {
      ...snapshot,
      generatedAt: officeChange.changedAt || new Date().toISOString(),
      work,
      employees,
    };
  }
  function iso(scene, x, y) {
    const helpers = window.FactoryLayeredRendererHelpers || {};
    if (helpers.isoPoint) return helpers.isoPoint(scene || SCENE, x, y);
    const active = scene || SCENE;
    return {
      x: active.originX + (x - y) * active.tileW / 2,
      y: active.originY + (x + y) * active.tileH / 2,
    };
  }
  function pointForZone(zone, seed) {
    const points = ZONE_POINTS[zone] || ZONE_POINTS.workshop;
    return points[hashStr(seed) % points.length];
  }
  function statusAction(status) {
    const clean = norm(status);
    if (clean.includes("fail") || clean.includes("error")) return "failed";
    if (clean.includes("block") || clean.includes("need")) return "blocked";
    if (clean.includes("complete") || clean.includes("done") || clean.includes("review")) return "completed";
    if (clean.includes("queue")) return "queued";
    if (clean.includes("delegat")) return "delegated";
    if (clean.includes("run") || clean.includes("work") || clean.includes("progress")) return "running";
    return "idle";
  }
  function colorForTone(tone) {
    const colors = {
      active: "#876947",
      blocked: "#9a4a38",
      failed: "#9a4a38",
      queued: "#6c6e75",
      delegated: "#715d86",
      done: "#55725a",
      dispatch: "#8a6a35",
      context: "#5e714d",
      mail: "#8a6338",
      sketch: "#8a5674",
      tool: "#5f6b66",
      idle: "#7a756b",
    };
    return colors[tone] || colors.active;
  }
  function snapshotEntities(snapshot, runtime, simT) {
    const scene = runtime && runtime.manifest && runtime.manifest.scene || SCENE;
    const bindings = runtime && runtime.bindings || {};
    const work = Array.isArray(snapshot && snapshot.work) ? snapshot.work.slice(0, 24) : [];
    const employees = Array.isArray(snapshot && snapshot.employees) ? snapshot.employees : [];
    const entities = [];
    const usedEmployeeIds = new Set();

    work.forEach((item, index) => {
      const runId = String(workRunId(item) || `work-${index}`);
      const category = workCategory(item);
      const action = statusAction(workStatus(item));
      const actionBinding = runtime && runtime.resolveAction ? runtime.resolveAction(action, { category, status: workStatus(item) }) : {};
      const zone = actionBinding.zone || (bindings.categories && bindings.categories[category] && bindings.categories[category].zone) || "workshop";
      const world = pointForZone(zone, runId);
      const wobble = action === "running" || action === "delegated" ? Math.sin(simT * 1.5 + index) * 0.08 : 0;
      const screen = iso(scene, world[0] + wobble, world[1]);
      const employee = employees.find((candidate) => String(candidate && candidate.id) === String(item.employeeId));
      if (item.employeeId) usedEmployeeIds.add(String(item.employeeId));
      entities.push({
        id: `run:${runId}`,
        x: screen.x,
        y: screen.y,
        depth: screen.y + index * 0.001,
        adapterKey: actionBinding.animation || "actor-placeholder",
        action: actionBinding.state || action,
        color: colorForTone(actionBinding.tone),
        width: 36,
        height: 56,
        scale: 1,
        shadowWidth: 18,
        shadowHeight: 5,
        name: employee && employee.name || "Worker",
        gender: employee && employee.gender || "",
        avatarKey: employee && employee.avatarKey || "",
        label: workTitle(item),
        runId,
        category,
        status: action,
      });
    });

    employees.filter((employee) => employee && employee.id && !usedEmployeeIds.has(String(employee.id))).slice(0, 10).forEach((employee, index) => {
      const world = pointForZone(isActiveEmployee(employee) ? "workshop" : "common", employee.id);
      const screen = iso(scene, world[0], world[1]);
      entities.push({
        id: `idle:${employee.id}`,
        x: screen.x,
        y: screen.y,
        depth: screen.y + index * 0.001,
        adapterKey: "actor-placeholder",
        action: "idle",
        color: colorForTone("idle"),
        width: 32,
        height: 50,
        name: employee.name || "Employee",
        gender: employee.gender || "",
        avatarKey: employee.avatarKey || "",
        label: employee.role || "Available",
        status: "idle",
      });
    });

    const walkK = (Math.sin(simT * 0.55) + 1) / 2;
    const walkX = 18.5 + walkK * 3.2;
    const walkY = 2.7 + Math.sin(simT * 0.55) * 0.12;
    const walker = iso(scene, walkX, walkY);
    entities.push({
      id: "qa-occlusion-walker",
      x: walker.x,
      y: walker.y,
      depth: walker.y - 12,
      adapterKey: "actor-placeholder",
      action: "running",
      color: "#8f6b45",
      width: 34,
      height: 54,
      name: "Occlusion QA",
      gender: "Female",
      avatarKey: "",
      label: "passes behind lathe foreground",
      status: "running",
    });

    return entities;
  }
  function fallbackSnapshot(db) {
    const employees = Array.isArray(db && db.employees) ? db.employees.slice(0, 12).map((employee) => ({
      id: employee.id,
      name: employee.name,
      role: employee.role || employee.title || "",
      gender: employee.gender || "",
      avatarKey: employee.avatarKey || "",
      state: "idle",
    })) : [];
    return { generatedAt: new Date().toISOString(), employees, work: [] };
  }

  function GraphicsCanvas({ runtime, snapshot, qaOverlay }) {
    const canvasRef = useRef(null);
    const rendererRef = useRef(null);
    const frameRef = useRef(0);
    const previousRef = useRef(0);

    useEffect(() => {
      if (!runtime || !canvasRef.current || !window.FactoryLayeredRenderer) return undefined;
      const canvas = canvasRef.current;
      const scene = runtime.manifest && runtime.manifest.scene || SCENE;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = scene.width * dpr;
      canvas.height = scene.height * dpr;
      const ctx = canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const renderer = new window.FactoryLayeredRenderer(ctx, runtime);
      renderer.setQaOverlay(qaOverlay);
      rendererRef.current = renderer;
      previousRef.current = performance.now();
      const loop = (now) => {
        const dt = Math.min(0.05, Math.max(0, (now - previousRef.current) / 1000));
        previousRef.current = now;
        renderer.setQaOverlay(qaOverlay);
        const simT = now / 1000;
        renderer.render({
          time: simT,
          deltaSeconds: dt,
          entities: snapshotEntities(snapshot, runtime, simT),
        });
        frameRef.current = requestAnimationFrame(loop);
      };
      frameRef.current = requestAnimationFrame(loop);
      return () => {
        cancelAnimationFrame(frameRef.current);
        renderer.dispose();
        rendererRef.current = null;
      };
    }, [runtime, snapshot, qaOverlay]);

    return <canvas ref={canvasRef} className="factory-canvas" aria-label="Appligator Factory graphics upgrade canvas" />;
  }

  function AppligatorFactoryGraphics({ db, onError }) {
    const [snapshot, setSnapshot] = useState(null);
    const [runtime, setRuntime] = useState(null);
    const [loading, setLoading] = useState(true);
    const [runtimeError, setRuntimeError] = useState("");
    const [manifestName, setManifestName] = useState(() => new URLSearchParams(window.location.search).get("factoryGraphics") || localStorage.getItem("factoryGraphicsManifest") || "production-v1");
    const [quality, setQuality] = useState(() => localStorage.getItem("factoryGraphicsQuality") || "balanced");
    const [qaOverlay, setQaOverlay] = useState(() => localStorage.getItem("factoryGraphicsQa") === "true");
    const [liveState, setLiveState] = useState("snapshot");
    const mountedRef = useRef(true);
    const refreshTimerRef = useRef(null);
    const snapshotRef = useRef(null);

    const loadSnapshot = useCallback(async (silent) => {
      if (!window.API || !window.API.getActionCenterOffice) {
        const fallback = fallbackSnapshot(db);
        snapshotRef.current = fallback;
        setSnapshot(fallback);
        setLiveState("fallback");
        setLoading(false);
        return;
      }
      try {
        if (!silent) setLoading(true);
        const next = await window.API.getActionCenterOffice();
        if (!mountedRef.current) return;
        snapshotRef.current = next || fallbackSnapshot(db);
        setSnapshot(snapshotRef.current);
        setLiveState(silent ? "live" : "snapshot");
      } catch (error) {
        if (onError) onError(error);
        const fallback = fallbackSnapshot(db);
        snapshotRef.current = fallback;
        setSnapshot(fallback);
        setLiveState("fallback");
      } finally {
        if (mountedRef.current && !silent) setLoading(false);
      }
    }, [db, onError]);

    useEffect(() => {
      mountedRef.current = true;
      loadSnapshot(false);
      return () => {
        mountedRef.current = false;
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      };
    }, [loadSnapshot]);

    useEffect(() => {
      if (!window.API || !window.API.subscribeToChanges) return undefined;
      const scheduleRefresh = () => {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = setTimeout(() => {
          refreshTimerRef.current = null;
          loadSnapshot(true);
        }, 240);
      };
      return window.API.subscribeToChanges((change) => {
        const officeChange = officeChangeFromChange(change);
        if (officeChange && officeChange.workItem && !officeChange.requiresSnapshotRefresh) {
          const merged = mergeWorkItemIntoSnapshot(snapshotRef.current, officeChange);
          if (merged) {
            snapshotRef.current = merged;
            setSnapshot(merged);
            setLiveState("live");
            return;
          }
        }
        if (officeChange || isFactoryRefreshChange(change)) scheduleRefresh();
      }, {
        onOpen: () => setLiveState("live"),
        onError: () => {
          setLiveState("stale");
          scheduleRefresh();
        },
      });
    }, [loadSnapshot]);

    useEffect(() => {
      let cancelled = false;
      async function loadRuntime() {
        if (!window.FactoryGraphicsLoader || !window.FactoryLayeredRenderer) {
          setRuntimeError("Factory graphics runtime has not been loaded.");
          return;
        }
        setRuntimeError("");
        const next = await window.FactoryGraphicsLoader.loadRuntime({
          manifest: manifestName,
          bindings: "default",
          quality: { level: quality, qaOverlay },
          preload: true,
        });
        if (cancelled) return;
        setRuntime(next);
      }
      loadRuntime().catch((error) => {
        if (!cancelled) {
          setRuntime(null);
          setRuntimeError(error && error.message || "Failed to load graphics runtime.");
          if (onError) onError(error);
        }
      });
      return () => { cancelled = true; };
    }, [manifestName, quality, qaOverlay, onError]);

    useEffect(() => { localStorage.setItem("factoryGraphicsManifest", manifestName); }, [manifestName]);
    useEffect(() => { localStorage.setItem("factoryGraphicsQuality", quality); }, [quality]);
    useEffect(() => { localStorage.setItem("factoryGraphicsQa", String(qaOverlay)); }, [qaOverlay]);

    const counts = useMemo(() => ({
      employees: Array.isArray(snapshot && snapshot.employees) ? snapshot.employees.length : 0,
      work: Array.isArray(snapshot && snapshot.work) ? snapshot.work.length : 0,
    }), [snapshot]);
    const sceneStyle = useMemo(() => {
      const scene = runtime && runtime.manifest && runtime.manifest.scene || SCENE;
      return { width: scene.width, height: scene.height };
    }, [runtime]);

    return (
      <section className="factory-page factory-package-page" data-appligator-id="appligator-factory-graphics">
        <div className="factory-package-shell">
          <div className="factory-package-stage">
            <div className="factory-view-controls" data-appligator-id="factory-graphics-controls">
              <button type="button" className={manifestName === "production-v1" ? "active" : ""} onClick={() => setManifestName("production-v1")}><Icon name="palette" size={13} />Production</button>
              <button type="button" className={manifestName === "placeholder" ? "active" : ""} onClick={() => setManifestName("placeholder")}><Icon name="tool" size={13} />Fallback</button>
              <select value={quality} onChange={(event) => setQuality(event.target.value)} aria-label="Factory graphics quality">
                <option value="low">Low</option>
                <option value="balanced">Balanced</option>
                <option value="high">High</option>
              </select>
              <button type="button" className={qaOverlay ? "active" : ""} onClick={() => setQaOverlay((value) => !value)}><Icon name="maximize" size={13} />QA</button>
              <button type="button" onClick={() => loadSnapshot(false)}><Icon name="refresh" size={13} />Refresh</button>
            </div>
            <div className="factory-scene-content" style={sceneStyle}>
              {runtime && snapshot && <GraphicsCanvas runtime={runtime} snapshot={snapshot} qaOverlay={qaOverlay} />}
              {(loading || runtimeError || !runtime || !snapshot) && (
                <div className="factory-empty loading">
                  <Icon name={runtimeError ? "alert" : "refresh"} size={20} />
                  <strong>{runtimeError ? "Graphics runtime unavailable" : "Loading graphics factory"}</strong>
                  <span>{runtimeError || "Reading the Office snapshot and graphics manifest."}</span>
                </div>
              )}
            </div>
          </div>
          <aside className="factory-side">
            <div className="factory-panel">
              <strong>Graphics pipeline</strong>
              <span>{runtime && runtime.manifest ? runtime.manifest.label : "Loading manifest"}</span>
              <div className="factory-stat-grid">
                <div><b>{counts.employees}</b><span>employees</span></div>
                <div><b>{counts.work}</b><span>work items</span></div>
                <div><b>{liveState}</b><span>event feed</span></div>
                <div><b>{quality}</b><span>quality</span></div>
              </div>
            </div>
            <div className="factory-panel">
              <strong>Migration proof points</strong>
              <span>Separate view, same Office snapshot contract.</span>
              <ul className="factory-mini-list">
                <li>Manifest switch keeps placeholder fallback available.</li>
                <li>Workers render through visual bindings and adapters.</li>
                <li>Lathe/press use layered machine parts.</li>
                <li>The QA walker passes behind the lathe foreground layer.</li>
              </ul>
            </div>
          </aside>
        </div>
      </section>
    );
  }

  window.AppligatorFactoryGraphics = AppligatorFactoryGraphics;
})();
